import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaChevronLeft, FaChevronRight, FaCrown } from "react-icons/fa";
import { FiNavigation, FiMapPin } from "react-icons/fi";
import { api, priceText, thumb, imgFallback } from "../api";
import { useAuth } from "../context/AuthContext";
import { regionLabel } from "../data/regions";
import ContactModal from "./product/ContactModal";
import TrustBadges from "./TrustBadges";
import { specSummary } from "../lib/listingFilters";
import "./NearbyListings.css";

const RADII = [5, 10, 25, 50];
const DEFAULT_AREA = "lagos:ikeja";
const STORE = "th-near";
const slug = (s) => String(s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const readSaved = () => { try { return JSON.parse(localStorage.getItem(STORE)) || {}; } catch { return {}; } };
const save = (v) => { try { localStorage.setItem(STORE, JSON.stringify({ ...readSaved(), ...v })); } catch { /* private mode */ } };

const getPosition = () => new Promise((resolve, reject) => {
  if (!navigator.geolocation) return reject(new Error("unsupported"));
  navigator.geolocation.getCurrentPosition((p) => resolve(p.coords), reject, { enableHighAccuracy: false, timeout: 10000, maximumAge: 10 * 60 * 1000 });
});

const away = (km) => (km < 1 ? "1 km away" : `${Number.isInteger(km) ? km : km.toFixed(1)} km away`);

export default function NearbyListings() {
  const { user, isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const me = String(user?._id || user?.id || "");

  const saved = useMemo(readSaved, []);
  const [areas, setAreas] = useState([]);
  const [mode, setMode] = useState(null);                  // "gps" | "area" (null until we know)
  const [pos, setPos] = useState(null);                    // { lat, lng } while in gps mode
  const [areaId, setAreaId] = useState(saved.area || null);
  const [radius, setRadius] = useState(RADII.includes(saved.radius) ? saved.radius : 10);
  const [data, setData] = useState(null);                  // last /nearby response
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [hint, setHint] = useState("");
  const [locating, setLocating] = useState(false);
  const [contact, setContact] = useState(null);

  // 1. the list of areas for the picker
  useEffect(() => { api("/places").then((d) => setAreas(d.areas || [])).catch(() => {}); }, []);

  // the shopper's profile area, if it matches one we know
  const profileArea = useMemo(() => {
    const st = slug(user?.location?.state), city = slug(user?.location?.city);
    if (!city) return null;
    return areas.find((a) => a.region === st && slug(a.name) === city)?.id || null;
  }, [areas, user]);

  const fallbackArea = useCallback(() => areaId || profileArea || DEFAULT_AREA, [areaId, profileArea]);

  const locateMe = useCallback(async (userAsked) => {
    setLocating(true); setHint("");
    try {
      const c = await getPosition();
      setPos({ lat: c.latitude, lng: c.longitude }); setMode("gps");
      if (userAsked) save({ mode: "gps" });
    } catch (e) {
      setMode("area");
      setHint(e && e.code === 1
        ? "Location access is blocked. Allow it in your browser settings for results closest to you, or pick an area."
        : "We couldn't read your location. Pick an area instead.");
    } finally { setLocating(false); }
  }, []);

  // 2. first load: ask for location unless the shopper already chose an area themselves or blocked it
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      if (saved.mode === "area" || !navigator.geolocation) return setMode("area");
      let state = "prompt";
      try { state = (await navigator.permissions?.query({ name: "geolocation" }))?.state || "prompt"; } catch { /* Safari */ }
      if (state === "denied") { setHint("Location is off. Showing a popular area; allow location for results closest to you."); return setMode("area"); }
      locateMe(false);
    })();
  }, [saved.mode, locateMe]);

  // 3. fetch listings whenever where / how far changes
  useEffect(() => {
    if (!mode) return;
    if (mode === "gps" && !pos) return;
    let live = true;
    setLoading(true); setErr("");
    const q = mode === "gps" ? `lat=${pos.lat}&lng=${pos.lng}` : `area=${encodeURIComponent(fallbackArea())}`;
    api(`/nearby?${q}&radius=${radius}&limit=12`)
      .then((d) => live && setData(d))
      .catch((e) => {
        if (!live) return;
        if (mode === "gps" && e.status === 422) { setHint("We only list items in Nigeria for now. Showing a popular area instead."); setMode("area"); return; }
        setErr(e.message || "Couldn't load nearby listings.");
      })
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [mode, pos, radius, fallbackArea]);

  const onPick = (e) => {
    const v = e.target.value;
    if (v === "gps") return locateMe(true);
    setAreaId(v); setMode("area"); setHint(""); save({ mode: "area", area: v });
  };
  const onRadius = (r) => { setRadius(r); save({ radius: r }); };

  // picker options grouped by state
  const groups = useMemo(() => {
    const m = new Map();
    for (const a of areas) { if (!m.has(a.region)) m.set(a.region, []); m.get(a.region).push(a); }
    return [...m.entries()].sort((x, y) => regionLabel(x[0]).localeCompare(regionLabel(y[0])));
  }, [areas]);

  // carousel
  const rail = useRef(null);
  const [edge, setEdge] = useState({ start: true, end: true });
  const measure = useCallback(() => {
    const el = rail.current; if (!el) return;
    setEdge({ start: el.scrollLeft < 8, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 8 });
  }, []);
  useEffect(() => { measure(); window.addEventListener("resize", measure); return () => window.removeEventListener("resize", measure); }, [measure, data, loading]);
  const scroll = (dir) => { const el = rail.current; if (el) el.scrollBy({ left: dir * Math.max(260, el.clientWidth * 0.8), behavior: "smooth" }); };

  const askSeller = (item) => {
    if (!isLoggedIn) return navigate(`/login?next=${encodeURIComponent(`/product/${item._id}`)}`);
    setContact(item);
  };

  const listings = data?.listings || [];
  const selectValue = mode === "gps" ? "gps" : fallbackArea();
  const nextRadius = RADII.find((r) => r > radius);
  const where = mode === "gps" ? (data?.origin?.label ? `near ${data.origin.label}` : "you") : (areas.find((a) => a.id === fallbackArea())?.name || "this area");

  return (
    <section className="nb-section">
      <div className="container">
        <div className="nb-head">
          <div className="nb-head-l">
            <span className="nb-tag"><FiNavigation /> Local marketplace</span>
            <h2 className="nb-title">Listings Near You</h2>
            <p className="nb-sub">Verified items available for immediate inspection, pickup, or same-day dispatch</p>
          </div>

          <div className="nb-filter">
            <FiMapPin className="nb-filter-pin" aria-hidden="true" />
            <select className="nb-select" value={selectValue} onChange={onPick} aria-label="Choose your location" disabled={locating}>
              <option value="gps">📍 {locating ? "Finding you…" : mode === "gps" && data?.origin?.label ? `My location (near ${data.origin.label})` : "Use my current location"}</option>
              {groups.map(([region, list]) => (
                <optgroup key={region} label={regionLabel(region)}>
                  {list.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
                </optgroup>
              ))}
              {areas.length === 0 && <option value={DEFAULT_AREA}>Ikeja (Computer Village / Allen)</option>}
            </select>
            <span className="nb-radius-l">Radius:</span>
            <div className="nb-radii" role="group" aria-label="Search radius">
              {RADII.map((r) => <button type="button" key={r} className={r === radius ? "on" : ""} onClick={() => onRadius(r)} aria-pressed={r === radius}>{r} km</button>)}
            </div>
          </div>
        </div>

        {hint && <p className="nb-hint">{hint}{mode === "area" && navigator.geolocation && <button type="button" onClick={() => locateMe(true)}>Use my location</button>}</p>}

        <div className="nb-stage">
          {!edge.start && <button type="button" className="nb-arrow nb-arrow-l" onClick={() => scroll(-1)} aria-label="Previous"><FaChevronLeft /></button>}

          <div className="nb-rail" ref={rail} onScroll={measure}>
            {loading && [0, 1, 2, 3].map((i) => <div key={i} className="nb-card nb-skel" />)}

            {!loading && err && (
              <div className="nb-empty"><strong>Couldn't load nearby listings</strong><span>{err}</span><button type="button" className="nb-empty-btn" onClick={() => setRadius((r) => r)} disabled>Try again later</button></div>
            )}

            {!loading && !err && listings.length === 0 && (
              <div className="nb-empty">
                <strong>No listings within {radius} km of {where} yet</strong>
                <span>Try a wider radius, or choose another area.</span>
                <div className="nb-empty-actions">
                  {nextRadius && <button type="button" className="nb-empty-btn" onClick={() => onRadius(nextRadius)}>Search {nextRadius} km</button>}
                  <Link to="/dashboard/post" className="nb-empty-link">Post an item</Link>
                </div>
              </div>
            )}

            {!loading && !err && listings.map((it) => {
              const mine = me && it.seller?.id === me;
              const meta = [it.condition, it.type].filter((v, i, a) => v && a.indexOf(v) === i).join(" • ");
              return (
                <article className="nb-card" key={it._id}>
                  <Link to={`/product/${it._id}`} className="nb-img" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
                    <img src={thumb(it.images?.[0] || "/placeholder.jpg", 480)} alt={it.title} loading="lazy" decoding="async" onError={imgFallback(it.images?.[0])} />
                    <span className="nb-dist"><FiMapPin /> {away(it.distanceKm)}</span>
                    <span className="nb-cat">{it.category}</span>
                    {it.promoted && <span className="nb-ad"><FaCrown /> Ad</span>}
                  </Link>
                  <div className="nb-body">
                    <p className="nb-price">{priceText(it)}</p>
                    <Link to={`/product/${it._id}`} className="nb-name">{it.title}</Link>
                    {(specSummary(it) || meta) && <p className="nb-meta">{specSummary(it) || meta}</p>}
                    <TrustBadges trust={it.trust} />
                    <p className="nb-loc"><FiMapPin /> {it.city}{it.region ? `, ${regionLabel(it.region)}` : ""}</p>
                    <div className="nb-seller">
                      {it.seller?.avatar ? <img src={it.seller.avatar} alt="" /> : <span className="nb-seller-i">{(it.seller?.name || "?")[0].toUpperCase()}</span>}
                      <span className="nb-seller-n">{it.seller?.name || "Seller"}</span>
                    </div>
                    {mine
                      ? <Link to={`/product/${it._id}`} className="nb-btn nb-btn-ghost">Your listing</Link>
                      : <button type="button" className="nb-btn" onClick={() => askSeller(it)} disabled={!it.seller}>Contact Seller</button>}
                  </div>
                </article>
              );
            })}
          </div>

          {!edge.end && <button type="button" className="nb-arrow nb-arrow-r" onClick={() => scroll(1)} aria-label="Next"><FaChevronRight /></button>}
        </div>
      </div>

      {contact && <ContactModal mode="message" p={contact} seller={contact.seller} onClose={() => setContact(null)} onOfferSent={() => {}} />}
    </section>
  );
}
